/**
 * Shared camera setup for the @zxing/browser barcode scanners.
 *
 * Two device-specific causes of a blurry preview are handled here:
 *
 *  - Low default resolution. getUserMedia hands back ~640x480 unless asked for
 *    more. On phones whose rear lens cannot focus very close (e.g. iPhone 12
 *    Pro Max), operators move in past the minimum focus distance to fill the
 *    frame and the image goes soft. A 1440p feed lets them hold the phone
 *    farther back — beyond the focus floor — and still resolve the bars.
 *  - Fixed focus. Many Android devices open the camera in a fixed-focus mode;
 *    requesting continuous autofocus on the live track sharpens them.
 */
import {
    BarcodeFormat,
    DecodeHintType,
    NotFoundException,
} from '@zxing/library'
import { BrowserMultiFormatReader } from '@zxing/browser'
import { createErrorDeduper } from './scanner-error-dedupe'
import type { IScannerControls } from '@zxing/browser'

/**
 * The per-frame "no code in view" signal from ZXing. Minification mangles
 * `err.name` in the production bundle (the class becomes `t`), so a name
 * check only works in dev — detect by class identity first, then by the
 * exception's own `kind` marker, then by message as the last line of defence
 * (covers a duplicated @zxing/library copy breaking instanceof).
 */
function isPerFrameNotFound(err: unknown): boolean {
    if (err instanceof NotFoundException) return true
    const ctor = (err as { constructor?: { kind?: string } })?.constructor
    if (ctor?.kind === 'NotFoundException') return true
    if (!(err instanceof Error)) return false
    return (
        err.name === 'NotFoundException' || // dev bundle keeps the class name
        err.message.includes('No MultiFormat Readers were able to detect')
    )
}

// Hi-res camera constraints. Budget Android cameras often expose NO programmatic
// zoom range at lower resolutions — operators are stuck between holding the
// phone too close (inside the lens' focus floor → blur) or too far (in focus but
// too few pixels to decode). 1440p gives the held-back, in-focus frame enough
// detail to resolve the bars. `ideal` so weak devices degrade gracefully.
export const BARCODE_CAMERA_CONSTRAINTS: MediaTrackConstraints = {
    facingMode: 'environment',
    width: { ideal: 2560 },
    height: { ideal: 1440 },
}

// Camera cho màn chụp ảnh khuôn mặt hộ trồng. Khác hẳn bộ trên: người dùng tự
// chụp mình nên cần camera TRƯỚC, và 720p là đủ (không phải đọc nét từng vạch
// như quét mã).
//
// `{ ideal: 'user' }` chứ KHÔNG `{ exact: 'user' }`: máy không có camera trước
// (máy tính bàn, một số máy tính bảng) sẽ ném OverconstrainedError với `exact`
// và không mở được camera nào cả. Với `ideal`, trình duyệt tự rơi về camera
// đang có — tức là cam sau.
//
// 1280x720 vì mặc định của getUserMedia là ~640x480; ở kích thước đó khuôn mặt
// chiếm quá ít pixel và vector trích ra kém hẳn.
export const FACE_CAMERA_CONSTRAINTS: MediaTrackConstraints = {
    facingMode: { ideal: 'user' },
    width: { ideal: 1280 },
    height: { ideal: 720 },
}

// Restrict to a single family per session. Decoding all formats at once
// adds latency and false-positive risk. The two whitelists below cover every
// scan the app does today (7 product-packaging 1D formats + QR for traceability).
export const BARCODE_FORMATS_1D_AND_QR = [
    BarcodeFormat.EAN_13,
    BarcodeFormat.EAN_8,
    BarcodeFormat.UPC_A,
    BarcodeFormat.UPC_E,
    BarcodeFormat.CODE_128,
    BarcodeFormat.CODE_39,
    BarcodeFormat.ITF,
    BarcodeFormat.QR_CODE,
]
export const BARCODE_FORMATS_QR_ONLY = [BarcodeFormat.QR_CODE]

export type ScanFormat = '1d-and-qr' | 'qr-only'

export type ScannerHandle = {
    stop: () => Promise<void>
    getVideoTrack: () => MediaStreamTrack | null
}

export type CreateScannerOptions = {
    videoEl: HTMLVideoElement
    formats: ScanFormat
    onResult: (text: string, format: string) => void
    onError?: (err: unknown) => void
}

/**
 * Start a barcode/QR scanner on the given video element. Returns a handle to
 * stop the scanner and read the live MediaStreamTrack (used for zoom and
 * tap-to-focus). Rejects on permission denial / device errors — the caller
 * surfaces these to the operator and Sentry.
 *
 * The decode callback fires per frame; `onResult` only fires when a code is
 * decoded successfully (the per-frame `NotFoundException` from ZXing is
 * filtered out silently — that is the normal "no code in view" loop, not an
 * error the caller should see). Anything that IS an error reaches `onError`
 * once per distinct fault per session; see `createErrorDeduper`.
 */
export async function createBarcodeScanner(
    opts: CreateScannerOptions,
): Promise<ScannerHandle> {
    const hints = new Map<DecodeHintType, unknown>()
    hints.set(
        DecodeHintType.POSSIBLE_FORMATS,
        opts.formats === 'qr-only'
            ? BARCODE_FORMATS_QR_ONLY
            : BARCODE_FORMATS_1D_AND_QR,
    )
    hints.set(DecodeHintType.TRY_HARDER, true)

    const reader = new BrowserMultiFormatReader(hints)
    let controls: IScannerControls | null = null
    const reportError = createErrorDeduper((err) => opts.onError?.(err))

    controls = await reader.decodeFromConstraints(
        { video: BARCODE_CAMERA_CONSTRAINTS },
        opts.videoEl,
        (result, err) => {
            if (result) {
                opts.onResult(
                    result.getText(),
                    BarcodeFormat[result.getBarcodeFormat()],
                )
                return
            }
            // NotFoundException fires every frame the lib failed to decode —
            // that is the normal scanning loop, NOT an error.
            if (err && !isPerFrameNotFound(err)) {
                reportError(err)
            }
        },
    )

    return {
        stop: () => {
            // On a torch-capable track ZXing's stop() returns a promise: it
            // turns the torch off on the ended track, which Chrome on Android
            // rejects. The torch dies with the track, so drop the rejection and
            // do not wait for it.
            Promise.resolve(controls?.stop()).catch(() => {})
            controls = null
            return Promise.resolve()
        },
        getVideoTrack: () => {
            const stream = opts.videoEl.srcObject
            if (
                !stream ||
                typeof (stream as MediaStream).getVideoTracks !== 'function'
            )
                return null
            return (stream as MediaStream).getVideoTracks()[0] ?? null
        },
    }
}

// focusMode is part of the Media Capture and Streams spec but is not yet in
// TypeScript's DOM lib, so extend the constraint types locally.
type FocusConstraintSet = MediaTrackConstraintSet & { focusMode?: string }
type FocusVideoConstraints = MediaTrackConstraints & {
    advanced?: FocusConstraintSet[]
}

const CONTINUOUS_FOCUS: FocusVideoConstraints = {
    advanced: [{ focusMode: 'continuous' }],
}

/**
 * Ask the running camera track for continuous autofocus. Best-effort: devices
 * that do not expose focusMode (notably iOS Safari, which already autofocuses
 * on its own) reject the request, which is intentionally ignored. Returns
 * whether the device honoured the request so the caller can feed the outcome
 * into the camera diagnostics report — a rejection here on an Android that
 * opened in fixed-focus mode is exactly the "some phones stay blurry" case.
 */
export async function applyContinuousFocus(
    track: MediaStreamTrack,
): Promise<boolean> {
    try {
        await track.applyConstraints(CONTINUOUS_FOCUS)
        return true
    } catch {
        /* focus control unsupported on this device — leave default focus */
        return false
    }
}

// Tap-to-focus. A normalized point (0..1 within the preview) the operator
// tapped, fed to the camera as a region to focus on. pointsOfInterest is part
// of the Media Capture spec but absent from TypeScript's DOM lib.
export interface FocusPoint {
    x: number
    y: number
}
type RefocusConstraintSet = FocusConstraintSet & {
    pointsOfInterest?: FocusPoint[]
}
type RefocusVideoConstraints = MediaTrackConstraints & {
    advanced?: RefocusConstraintSet[]
}

/**
 * Force a fresh focus pass, optionally aimed at the tapped point. This is the
 * escape hatch for budget Android cameras whose continuous autofocus hunts and
 * never locks on a barcode. When the device rejects single-shot (or
 * pointsOfInterest), fall back to kicking a continuous pass, which more
 * cameras honour. Best-effort and never throws.
 */
export async function triggerRefocus(
    track: MediaStreamTrack,
    point?: FocusPoint,
): Promise<void> {
    const focusSet: RefocusConstraintSet = { focusMode: 'single-shot' }
    if (point) focusSet.pointsOfInterest = [point]
    try {
        await track.applyConstraints({
            advanced: [focusSet],
        } as RefocusVideoConstraints)
    } catch {
        await applyContinuousFocus(track)
    }
}

// Camera zoom. A small 1D barcode (e.g. on a tea box) covers too few pixels to
// decode; zooming in fills the frame with the bars instead of forcing the
// operator to move past the lens' minimum focus distance (where it blurs).
export interface ZoomCapability {
    min: number
    max: number
    step: number
}
type ZoomCapabilities = MediaTrackCapabilities & {
    zoom?: { min?: number; max?: number; step?: number }
}
type ZoomConstraintSet = MediaTrackConstraintSet & { zoom?: number }
type ZoomVideoConstraints = MediaTrackConstraints & {
    advanced?: ZoomConstraintSet[]
}

/**
 * Read the live track's zoom range, or null when zoom is unsupported (iOS
 * Safari, many low-end cameras) or the track is gone. A degenerate range
 * (max <= min) counts as unsupported. Drives whether the zoom slider renders.
 */
export function getZoomCapability(
    track: MediaStreamTrack | null,
): ZoomCapability | null {
    if (!track) return null
    try {
        const zoom = (track.getCapabilities() as ZoomCapabilities).zoom
        if (
            !zoom ||
            typeof zoom.min !== 'number' ||
            typeof zoom.max !== 'number' ||
            zoom.max <= zoom.min
        ) {
            return null
        }
        return { min: zoom.min, max: zoom.max, step: zoom.step ?? 0.1 }
    } catch {
        return null
    }
}

/**
 * Set the live track's zoom level. Best-effort: ignored on devices without
 * zoom control (mirrors applyContinuousFocus).
 */
export async function applyZoom(
    track: MediaStreamTrack,
    zoom: number,
): Promise<void> {
    try {
        await track.applyConstraints({
            advanced: [{ zoom }],
        } as ZoomVideoConstraints)
    } catch {
        /* zoom unsupported on this device — leave default zoom */
    }
}

type FocusCapabilities = MediaTrackCapabilities & { focusMode?: string[] }
type FocusSettings = MediaTrackSettings & { focusMode?: string }

/**
 * One info event per camera start describing which rear lens the browser
 * bound and what focus/zoom control it exposes. A blurry-preview report from
 * a store phone has several possible causes that throw NO error anywhere:
 * the browser picked a fixed-focus/ultrawide lens (multi-camera Androids),
 * the continuous-autofocus request was rejected, or the lens has no zoom to
 * compensate with. This event is the only signal that tells WHICH of those a
 * given device hit. Best-effort: diagnostics must never break scanning.
 */
export function reportScannerCameraDiagnostics(
    track: MediaStreamTrack,
    continuousFocusAccepted: boolean,
    capture: (message: string, data: Record<string, unknown>) => void,
): void {
    try {
        const settings = track.getSettings() as FocusSettings
        let supportedFocusModes: string[] | null = null
        try {
            supportedFocusModes =
                (track.getCapabilities() as FocusCapabilities).focusMode ?? null
        } catch {
            /* getCapabilities unsupported (Firefox) — report the rest */
        }
        capture('barcode scanner camera diagnostics', {
            cameraLabel: track.label,
            deviceId: settings.deviceId,
            width: settings.width,
            height: settings.height,
            frameRate: settings.frameRate,
            settingsFocusMode: settings.focusMode ?? null,
            supportedFocusModes,
            zoom: getZoomCapability(track),
            continuousFocusAccepted,
        })
    } catch {
        /* never let diagnostics break the scanner */
    }
}

/**
 * Self-check after the camera starts. A CSS/layout regression can leave the
 * mounted <video> at zero size — the camera runs, decodes nothing visible,
 * the operator just sees a black box, and crucially NO error is thrown. So
 * this would ship silently and we only hear about it from a store phone call.
 * Measure the rendered video one frame after start and report a zero
 * dimension to Sentry, turning a silent black screen into an alert.
 *
 * Kept after the ZXing migration as a cheap safety net even though ZXing no
 * longer mounts its own DOM (we own the <video> ref).
 */
export function reportIfScannerVideoCollapsed(
    videoEl: HTMLVideoElement | null,
    capture: (error: Error) => void,
): void {
    if (!videoEl) return
    requestAnimationFrame(() => {
        const { width, height } = videoEl.getBoundingClientRect()
        if (width === 0 || height === 0) {
            capture(
                new Error(
                    `barcode scanner video rendered at zero size (${width}x${height}) — camera is live but invisible to the operator`,
                ),
            )
        }
    })
}
